import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren, ChangeDetectionStrategy, inject } from '@angular/core';
import { Order } from '../../interfaces/orders/order';
import { PartyUIConfiguration } from '../../interfaces/ui-configurations/party-ui-configuration';
import { map, Observable, Subject, take, takeUntil } from 'rxjs';
import { ValidationProblem } from '../../interfaces/validation/validation-problem';
import { Party } from '../../interfaces/parties/party';
import { DatePipe } from '@angular/common';
import { EmptyEntityService } from '../../services/empty-entity-service/empty-entity.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ePartyType, eGeneration, eOrderType, ePepsAndSanctionListGroup } from '../../enums';
import { ReusableParty } from '../../interfaces/reusable-parties/reusable-party';
import { PartyForCreation } from '../../interfaces/parties/party-for-creation';
import { Utilities } from '../../utilities';
import { PartiesFormComponent } from '../../ui-common/parties-form/parties-form.component';
import { PartyRepositoryService } from '../../services/party/party-repository.service';
import { MatDialog } from '@angular/material/dialog';
import { PartyPepsAndSanctionsDialogComponent } from '../party-peps-dialog/party-peps-and-sanctions-dialog.component';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { PatchDocument } from '../../interfaces/patch-document';
import { OrderManagerService } from '../../services/order-manager/order-manager.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'lib-parties-list',
    templateUrl: './parties-list.component.html',
    styleUrls: ['./parties-list.component.css'],
    providers: [DatePipe],
    animations: [
        trigger('detailExpand', [
            state('collapsed,void', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PartiesListComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  @ViewChild('partiesListComponentRoot', { static: true }) component!: ElementRef;
  @ViewChildren('menuContainer', {read: ElementRef}) menuRefs!: QueryList<ElementRef>;
  @ViewChildren(PartiesFormComponent) PartyFormComponents!: QueryList<PartiesFormComponent>;

  @Input() order!: Order;
  @Input() uiConfiguration!: PartyUIConfiguration;
  @Input() errors$!: Observable<ValidationProblem[] | undefined>;
  @Input() expandedParty: Party | null = null;

  @Input() isEditable: boolean = false;
  @Input() isAdmin: boolean = false;

  @Input() businessPartyType!: ePartyType;
  @Input() individualPartyType!: ePartyType;
  @Input() partyTypeTitle: 'Debtor' | 'Secured Party' | 'Dealer' | 'Registering Agent' | 'Signing Authority' | 'Party' | 'Assignor' | 'Assignee' = 'Party';
  @Input() defaultPartyType: ePartyType = this.businessPartyType;

  @Output() partyAddedEvent = new EventEmitter<Party>();
  @Output() partyUpdatedEvent = new EventEmitter<Party>();
  @Output() partyCopiedEvent = new EventEmitter<Party>();
  @Output() partyRemovedEvent = new EventEmitter<Party>();
  @Output() partyFormValueChangedEvent = new EventEmitter<Party>();
  @Output() reusablePartySavedEvent = new EventEmitter<PartyForCreation>();
  @Output() reusableLinkedPartiesSavedEvent = new EventEmitter<PartyForCreation[]>();

  @Input() observableParties$!: Observable<Party[]>;
  @Input() reusableParties: ReusableParty[] = [];

  displayedColumns: string[] = ['name'];
  isLoading: boolean = false;

  linkingPartyID: string | null = null;
  highlightedPartyIDs: string[] = [];
  highlightLock = false;
  hoveredParty: Party | null = null;

  private parties: Party[] = [];
  private onDestroy$ = new Subject<void>();

  constructor(
    private datePipe: DatePipe,
    private emptyEntityService: EmptyEntityService,
    private partiesService: PartyRepositoryService,
    private dialog: MatDialog,
    private orderManager: OrderManagerService) { }

  ngOnInit(): void {
    if (!this.uiConfiguration.hidePartyNumber) {
      this.displayedColumns.unshift('num');
    }

    if (!this.uiConfiguration.hidePartyType) {
      this.displayedColumns.push('party-type');
    }

    if (this.order.orderStatusTypeID.toLocaleLowerCase() != 'draft' && this.uiConfiguration.showJobTitle) {
      this.displayedColumns = this.displayedColumns.concat(['job-title']);
    }
    
    if (this.uiConfiguration.hasContactDetails) {
      this.displayedColumns = this.displayedColumns.concat(['contactDetails']);
    }

    if (this.uiConfiguration.showEstate) {
      this.displayedColumns = this.displayedColumns.concat(['estate']);
    }

    if (this.uiConfiguration.showRegistryCode) {
      this.displayedColumns = this.displayedColumns.concat(['registry-code']);
    }

    if (this.uiConfiguration.showSanctions && 
        this.order.orderStatusTypeID.toLocaleLowerCase() != 'draft' &&
        //Default to true for non KYC orders
        (this.order.knowYourCustomerCriteria?.shouldDoPepsAndSanctions ?? true) === true) {
      this.displayedColumns = this.displayedColumns.concat(['sanction-records']);
    }

    if (this.uiConfiguration.showPEPS && 
        this.order.orderStatusTypeID.toLocaleLowerCase() != 'draft' && 
        //Default to true for non KYC orders
        (this.order.knowYourCustomerCriteria?.shouldDoPepsAndSanctions ?? true) === true) {
      this.displayedColumns = this.displayedColumns.concat(['pep-records']);
    }

    if (this.uiConfiguration.showPostRegistrationSearchCheckbox) {
      this.displayedColumns = this.displayedColumns.concat(['post-search']);
    }

    if (this.isEditable) {
      this.displayedColumns = this.displayedColumns.concat(['actions']);
    }

    this.observableParties$ = this.observableParties$.pipe(
      map(parties => parties.sort((a, b) => a.priority - b.priority)));

    this.observableParties$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(parties => {
        this.parties = parties;

        if(this.uiConfiguration.allowAddingPartyLinks ||
          this.uiConfiguration.allowDeletingNewPartyLinks ||
          this.uiConfiguration.allowDeletingOldPartyLinks) {
            if(this.parties.some(p => (p.linkedParties?.length ?? 0) > 0)) {
              if(!this.displayedColumns.includes('linked')) {
                this.displayedColumns.unshift('linked');
              }
            } else {
              if(this.displayedColumns.includes('linked')) {
                this.displayedColumns.shift();
              }
            }
          }
      });
  }

  onSaving(): Party | null {
    if (this.expandedParty) {
      const expandedComponent = this.PartyFormComponents.find(component => component.party === this.expandedParty);

      if (expandedComponent) {
        return expandedComponent.getParty();
      }
    }

    return null;
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onRowClick(event: Event, party: Party) {
    if (this.linkingPartyID) {
      const linkingPartyID = this.linkingPartyID;
      this.linkingPartyID = null;

      //If we're not linking a party or the party we're trying to link to is already the target of a link, just do nothing
      if(this.parties.some(p => p.linkedParties?.some(lp => lp.linkedPartyID === party.id))) {
        return;
      }

      this.partiesService.linkParties(party.id, linkingPartyID).subscribe(link => {
        party.linkedParties?.push(link);

        if (!this.displayedColumns.includes('linked')) {
          this.displayedColumns.unshift('linked');
        }
      });
      
    } else {
      this.onEditButtonClick(event, party);
    }
  }

  onEditButtonClick = (event: Event, partyToEdit: Party) => {
    if (this.isEditable) {
      this.expandedParty = this.expandedParty === partyToEdit ? null : partyToEdit;
    }
  }

  onDeleteButtonClick = (event: Event, partyToDelete: Party, rowIndex: number) => {
    // emit this party to delete
    this.partyRemovedEvent.emit(partyToDelete);
  }

  onCopyButtonClick = (event: Event, partyToCopy: Party) => {
    const priority = this.getNewPriority();
    const copiedParty = { ...partyToCopy, priority };
    copiedParty.shouldTriggerPostRegistrationSearch = false;
    this.partyCopiedEvent.emit(copiedParty);
  }

  handlePartyUpdateEvent(party: Party) {
    const localParty = this.parties.find(p => p.id === party.id);

    if (localParty) {
      party.shouldTriggerPostRegistrationSearch = localParty.shouldTriggerPostRegistrationSearch;
    }

    this.partyUpdatedEvent.emit(party);
    this.expandedParty = null;
  }

  handlePartyRemovedEvent(party: Party) {
    this.partyRemovedEvent.emit(party);
    this.expandedParty = null;
  }

  handleChangesCancelledEvent(party: Party) {
    this.expandedParty = null;
  }

  handlePartyFormValueChangedEvent(party: Party) {
    this.partyFormValueChangedEvent.emit(party);
  }

  handleExistingPartySaveEvent(existingParty: PartyForCreation) {
    existingParty.priority = this.getNewPriority();
    this.reusablePartySavedEvent.emit(existingParty);
  }

  handleExistingLinkedPartiesSaveEvent(existingParties: PartyForCreation[]) {
    var priority = this.getNewPriority();
    existingParties.forEach(p => {
      p.priority = priority;
      priority++;
    });
    this.reusableLinkedPartiesSavedEvent.emit(existingParties);
  }

  protected addNewParty() {
    const priority = this.getNewPriority();
    let newParty = this.emptyEntityService.getEmptyParty(this.defaultPartyType, this.order?.id ?? '', priority);
    this.partyAddedEvent.emit(newParty);
  }

  getName(party: Party) {
    let name: string | null | undefined;

    if (Utilities.isIndividual(party)) {
      var fullName = Utilities.getIndividualName(party, this.isAdmin);

      if (party.generationID && this.uiConfiguration.showGeneration) {
        fullName += `, ${eGeneration[party.generationID as keyof typeof eGeneration]}`;
      }

      if (party.dateOfBirth && this.uiConfiguration.showDateOfBirth) {
        let formattedDateOfBirth = this.datePipe.transform(party.dateOfBirth, 'yyyy-MM-dd');
        fullName += ` (${formattedDateOfBirth})`;
      }

      name = fullName.trim();
    } else {
      name = party.busName?.trim();
    }

    if (name) {
      return name;
    } else {
      var type = Utilities.getPartyRoleLabel(party);

      if (this.order?.orderTypeID === eOrderType.basicDischarge ||
        this.order?.orderTypeID === eOrderType.basicRenewal) {
        return type;
      } else if(this.order.orderTypeID === eOrderType.knowYourCustomer) {
        return '-';
      } else {
        return this.transloco.translate('partiesList.newPartyType', { type });
      }
    }
  }

  getAddress(party: Party) {
    return Utilities.formatAddress(party.contactDetails?.address, this.isAdmin);
  }

  getContactDetails(party: Party) {

    let result: string = this.getAddress(party);

    if (party.contactDetails?.email) {
      if (result) {
        result += '<br>';
      }

      result += `${this.transloco.translate('common.labels.email2')} ${this.isAdmin && this.uiConfiguration.contactDetailsConfiguration?.overrideEmailForAdmins ? 
        this.uiConfiguration.contactDetailsConfiguration?.emailOverride ?? party.contactDetails?.email : party.contactDetails?.email}`
    }

    if (party.contactDetails?.phoneNumber) {
      if (result) {
        result += '<br>';
      }

      result += `${this.transloco.translate('partiesList.phonePrefix')} ${party.contactDetails.phoneNumber}`
    }

    if (party.contactDetails?.faxNumber) {
      if (result) {
        result += '<br>';
      }

      result += `${this.transloco.translate('partiesList.faxPrefix')} ${party.contactDetails.faxNumber}`
    }

    return result;
  }

  isEstate(party: Party) {
    if (Utilities.isIndividual(party)) {
      return party.isEstate ? "YES" : "NO";
    } else {
      return "";
    }
  }

  getPartyTypeString(party: Party) {
    return Utilities.getPartyTypeString(party);
  }

  getPartyTypeIcon(party: Party) {
    return Utilities.getPartyTypeIcon(party);
  }

  /** Translation key suffix for partyTypeTitle, e.g. 'Secured Party' -> 'securedParty'. */
  get partyTypeKey(): string {
    const words = this.partyTypeTitle.trim().split(/\s+/);
    return words.map((w, i) => i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('');
  }

  pluralizePartyTypeTitle(): string {
    let title = this.partyTypeTitle.toLowerCase();

    if (title.endsWith('y')) {
      title = title.substring(0, title.length - 1) + 'ies';
    } else {
      title = title + 's';
    }

    return title;
  }

  private getNewPriority() {
    //Need the highest priority + 1
    let priority = this.parties
      .map(p => p.priority)
      .sort((a, b) => b - a)
      .at(0);

    if (priority === undefined || priority === 0) {
      priority = 1;
    } else {
      priority++;
    }

    return priority;
  }

  protected onLinkButtonClick(event: Event, party: Party) {
    this.linkingPartyID = party.id;
  }

  protected onUnlinkButtonClick(event: Event, party: Party) {
    const link = this.parties.flatMap(p => p.linkedParties)?.find(link => link?.linkedPartyID === party.id);

    if (!link || link.partyID === party.id) {
      return;
    }

    const parentParty = this.parties.find(p => p.id === link?.partyID);

    if (!parentParty) {
      return;
    }

    this.partiesService.unlinkParties(link.partyID, link.id).subscribe(() => {
      const indexOfLink = parentParty.linkedParties?.findIndex(l => l.id === link.id) ?? -1;

      if (indexOfLink >= 0) {
        parentParty.linkedParties?.splice(indexOfLink, 1);
      }

      const indexOfHighlightedParty = this.highlightedPartyIDs.findIndex(id => id === link.linkedPartyID);

      if (indexOfHighlightedParty >= 0) {
        this.highlightedPartyIDs.splice(indexOfHighlightedParty, 1);
      }

      if (!this.parties.some(p => (p.linkedParties?.length ?? 0) > 0) &&
        this.displayedColumns.includes('linked')) {
        this.displayedColumns.shift();
      }
    })
  }

  protected shouldShowAddLinkButton(party: Party) {
    return this.uiConfiguration.allowAddingPartyLinks &&
      this.parties.length > 1 &&
      (party.linkedParties === undefined ||
        party.linkedParties === null ||
        party.linkedParties.length === 0) &&
      !this.parties
        .filter(p => p.id !== party.id)
        .some(p => p.linkedParties?.some(link => link.linkedPartyID === party.id));
  }

  protected shouldShowUnlinkButton(party: Party) {
    return (this.isPartyNewlyLinked(party) &&
      this.uiConfiguration.allowDeletingNewPartyLinks) ||
      (this.isPartyLinked(party) &&
      this.uiConfiguration.allowDeletingOldPartyLinks);
  }

  protected shouldShowLinkedIcon(party: Party) {
    return (this.uiConfiguration.allowAddingPartyLinks ||
      this.uiConfiguration.allowDeletingNewPartyLinks ||
      this.uiConfiguration.allowDeletingOldPartyLinks) &&
      (party.linkedParties?.length ?? 0) > 0 ||
      this.parties.filter(p => p.id != party.id).some(p => p.linkedParties?.some(link => link.linkedPartyID === party.id));
  }

  protected onLinkIconClick(event: Event, party: Party) {
    this.highlightedPartyIDs = [];
    this.highlightParties(party);
    this.highlightLock = !this.highlightLock;
    this.onPartyRowEnter(party);
  }

  private highlightParties(sourceParty: Party) {
    if ((sourceParty.linkedParties?.length ?? 0) > 0) {
      this.highlightedPartyIDs.push(sourceParty.id);
      sourceParty.linkedParties?.forEach(link => this.highlightedPartyIDs.push(link.linkedPartyID));
    } else {
      const parentParty = this.parties.find(p => p.linkedParties?.some(link => link.linkedPartyID === sourceParty.id));

      if (parentParty) {
        this.highlightedPartyIDs.push(parentParty.id);
        this.highlightedPartyIDs.push(sourceParty.id);
      }
    }
  }

  protected shouldHighlight(party: Party) {
    return this.highlightedPartyIDs.findIndex(id => id === party.id) >= 0;
  }

  protected shouldHighlightDark(party: Party) {
    return this.shouldHighlight(party) && this.hoveredParty === party;
  }

  protected hasPEPs(party: Party) {
    return (party.pepRecords?.length ?? 0) > 0;
  }

  protected getPEPsIcon(party: Party) {
    return this.order.orderStatusTypeID.toLowerCase() === 'complete' ? 
      (this.hasPEPs(party) ? 'warning' : 'check_circle') :
      'pending';
  }

  protected openPEPsDialog(party: Party) {
    const dialogRef = this.dialog.open(PartyPepsAndSanctionsDialogComponent, {
          data: party,
          minWidth: '800px'
        });
  }

  protected hasSanctions(party: Party) {
    return (party.sanctionRecords?.length ?? 0) > 0;
  }

  protected getSanctionsIcon(party: Party) {
    if ((party.sanctionRecords?.length ?? 0) > 0) {
      return (party.sanctionRecords!.filter(r => r.listGroup == ePepsAndSanctionListGroup.Primary).length > 0) ?
        'cancel' : 'warning';
    }
    else if (this.order.orderStatusTypeID.toLowerCase() === 'complete') {
      return 'check_circle';
    }
    else {
      return 'pending';
    }
  }

  protected openSanctionsDialog(party: Party) {
    const dialogRef = this.dialog.open(PartyPepsAndSanctionsDialogComponent, {
          data: party,
          minWidth: '500px'
        });
  }

  protected onPartyRowEnter(party: Party) {
    if (!this.highlightLock) {
      this.highlightParties(party);
    }
  }

  protected onPartyRowLeave(party: Party) {
    if (!this.highlightLock) {
      this.highlightedPartyIDs = [];
    }
  }

  protected getCursor(party: Party): string {
    if(this.linkingPartyID) {
      return this.isPartyLinked(party) || this.linkingPartyID === party.id ? 'not-allowed' : 'cell';
    } else{
      return 'default';
    }
  }

  protected getOpacity(party: Party): string {
    if(this.linkingPartyID) {
      return this.isPartyLinked(party) || this.linkingPartyID === party.id ? '0.4' : '1';
    } else {
      return '1';
    }
  }

  protected getPepsColor(party: Party): string {
    return ((party.pepRecords?.length ?? 0) > 0) ? '#9A6C00' : '#5C7F46' ;
  }

  protected getSanctionsColor(party: Party): string {
    if ((party.sanctionRecords?.length ?? 0) > 0) {
      return (party.sanctionRecords!.filter(r => r.listGroup == ePepsAndSanctionListGroup.Primary).length > 0) ?
        '#FB2C38' : '#9A6C00';
    }
    else {
      return '#5C7F46' ;
    }
  }

  protected isPartyNewlyLinked(party: Party) {
    return this.parties.filter(p => p.id !== party.id).some(p => p.linkedParties?.some(lp => lp.linkedPartyID === party.id && lp.isNewLink));
  }

  protected isPartyLinked(party: Party) {
    return this.parties.filter(p => p.id !== party.id).some(p => p.linkedParties?.some(lp => lp.linkedPartyID === party.id));
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickedInside = this.component.nativeElement.contains(event.target) ||
      this.menuRefs.some(ref => ref.nativeElement.contains(event.target));

    if (!clickedInside && this.highlightLock) {
      this.highlightedPartyIDs = [];
      this.highlightLock = false;
    }

    if (!clickedInside) {
      this.linkingPartyID = null;
    }
  }

  getLinkedPartyTooltip(party: Party) {
    const linkedPartyCount = party.linkedParties?.length ?? 0;

    if (linkedPartyCount > 0) {
      const operators: string[] = [];

      for (let i = 0; i < linkedPartyCount; i++) {
        const linkedParty = this.parties.find(p => p.id === party.linkedParties?.at(i)?.linkedPartyID);

        if (linkedParty) {
          operators.push(Utilities.getName(linkedParty, false));
        }
      }

      return this.transloco.translate('partiesList.isOperatedBy', { name: Utilities.getName(party, false), operators: operators.join(', ') });
    } else {
      const parentParty = this.parties.find(p => p.linkedParties?.some(lp => lp.linkedPartyID === party.id));

      if (parentParty) {
        return this.transloco.translate('partiesList.isOperatingAs', { name: Utilities.getName(party, false), parentName: Utilities.getName(parentParty, false) });
      } else {
        return '';
      }
    }
  }

  onPostRegSearchCheckboxClick(party: Party, event: MatCheckboxChange) {
    party.shouldTriggerPostRegistrationSearch = event.checked;
    const patchDocument: PatchDocument[] = [ { op: 'replace', value: party.shouldTriggerPostRegistrationSearch, path: 'shouldTriggerPostRegistrationSearch' } ];

    this.partiesService.patchParty(this.order.id, party.id, patchDocument).subscribe((p: Party) => {
      const index = this.order.parties!.findIndex(obj => obj.id === p.id);

      if (index != -1) {
        this.order.parties![index] = p;
        this.orderManager.updateOrder(this.order);
      }
    });
  }
}
